import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileStack, Loader2, ExternalLink, Copy, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const VERTICALS = {
  beleza: "Beleza", saude: "Saúde", pet: "Pet", educacao: "Educação", assistencia: "Assistência",
};

export default function MasterTemplates() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/master/templates");
        const items = data.items || [];
        setList(items);
        // Fetch views per niche in parallel
        const all = await Promise.all(items.map((t) =>
          api.get(`/public/template-preview/${t.vertical}/stats`).then((r) => [t.vertical, r.data]).catch(() => [t.vertical, null])
        ));
        const m = {}; all.forEach(([v, s]) => { if (s) m[v] = s; });
        setStats(m);
      }
      finally { setLoading(false); }
    })();
  }, []);

  const copyShareLink = (vertical) => {
    const url = `${window.location.origin}/preview/${vertical}?utm_source=master-panel&utm_campaign=share`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success("Link de prévia copiado"),
      () => toast.error("Falha ao copiar. URL: " + url),
    );
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Modelos prontos</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Modelos prontos por segmento</h1>
        <p className="text-sm text-muted-foreground mt-1">Cada modelo carrega termos, recursos, regras de fidelidade, níveis e mensagens prontos. Compartilhe a prévia pública com prospects.</p>
      </div>

      {loading ? (
        <div className="text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {list.map((t) => {
            const s = stats[t.vertical];
            return (
              <Card key={t.id} className="p-6 rounded-xl border-border" data-testid={`template-card-${t.vertical}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileStack className="w-4 h-4" />
                    <Badge variant="outline">{VERTICALS[t.vertical] || t.vertical}</Badge>
                    <Badge>v{t.version}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString("pt-BR")}</div>
                </div>
                <div className="font-display text-xl font-semibold">{t.name}</div>
                <div className="text-sm text-muted-foreground mt-1">{t.description}</div>

                <div className="mt-4 flex items-center gap-2">
                  <div className="h-2 flex-1 rounded-full" style={{ background: `hsl(${t.default_branding?.primary_color})` }} />
                  <span className="text-xs text-muted-foreground">{t.default_modules?.length} recursos</span>
                </div>

                {s && (
                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <BarChart3 className="w-3.5 h-3.5" /> {s.total_views} visualizações da prévia
                    </span>
                    {s.by_source?.length > 0 && (
                      <span className="opacity-70 truncate max-w-[180px]" title={s.by_source.map((b) => `${b.utm_source}: ${b.count}`).join(" · ")}>
                        {s.by_source.slice(0, 2).map((b) => `${b.utm_source}(${b.count})`).join(" · ")}
                      </span>
                    )}
                  </div>
                )}

                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => window.open(`/preview/${t.vertical}?utm_source=master-panel`, "_blank")}
                    data-testid={`template-open-${t.vertical}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Ver prévia
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => copyShareLink(t.vertical)}
                    data-testid={`template-copy-${t.vertical}`}
                  >
                    <Copy className="w-3.5 h-3.5" /> Compartilhar
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
